import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SearchBar from '../components/SearchBar';
import HeaderBar from '../components/HeaderBar';
import CreatePatientModal from '../components/CreatePatientModal';
import { patientsService, Patient, CreatePatientData, PaginatedResponse, getPatientActivityStatus } from '../services/patientsService';
import { useAuth } from '../hooks/useAuth';
import { useIsMobile } from '../hooks/useIsMobile';
import ServiceStatusIcons from '../components/ServiceStatusIcons';

type WelcomeEmailType = 'welcome' | 'welcomeWithPassword' | 'welcomeMonarka' | 'welcomeMonarkaWithPassword';

const PatientsList: React.FC = () => {
  const [search, setSearch] = useState('');
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeMenu, setActiveMenu] = useState<number | null>(null);
  const [showConfirmation, setShowConfirmation] = useState<{
    userId: number;
    type: WelcomeEmailType;
    patientName: string;
    patientEmail: string;
  } | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const canSendMonarkaEmails =
    user?.role === 'admin' ||
    (user?.role === 'doctor' && user?.email?.toLowerCase().endsWith('@monarkaclinic.com'));
  const [searchParams, setSearchParams] = useSearchParams();
  const menuRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

  // Check for create query param on mount
  useEffect(() => {
    const shouldCreate = searchParams.get('create') === 'true';
    if (shouldCreate) {
      setShowCreateModal(true);
      // Clean up the URL by removing the query param
      searchParams.delete('create');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);


  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickedOutside = Object.values(menuRefs.current).every(
        ref => !ref || !ref.contains(event.target as Node)
      );
      if (clickedOutside) {
        setActiveMenu(null);
      }
    };

    if (activeMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeMenu]);

  const handleSendWelcomeEmail = async (userId: number, welcomeEmailType: WelcomeEmailType) => {
    setIsSending(true);
    try {
      await patientsService.sendWelcomeEmail(userId, welcomeEmailType);
    } catch (error) {
      alert('Error al enviar el email. Por favor, intenta nuevamente.');
      console.error('Error sending welcome email:', error);
    } finally {
      setIsSending(false);
      setShowConfirmation(null);
      setActiveMenu(null);
    }
  };

  const fetchPatients = async (page: number = 1, searchTerm: string = '') => {
    try {
      setLoading(true);
      const response: PaginatedResponse = await patientsService.getPatients(page, searchTerm);
      setPatients(response.results);
      setCurrentPage(response.actualPage);
      setTotalPages(response.totalPages);
    } catch (error) {
      console.error('Failed to fetch patients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePatient = async (data: CreatePatientData) => {
    try {
      await patientsService.createPatient(data);
      setShowCreateModal(false);
      await fetchPatients(1, search);
      setCurrentPage(1);
    } catch (error) {
      console.error('Failed to create patient:', error);
      throw error;
    }
  };

  // Debounce search: reset to page 1 and query the backend whenever the term changes
  useEffect(() => {
    const timeout = setTimeout(() => {
      setCurrentPage(1);
      fetchPatients(1, search);
    }, 300);

    return () => clearTimeout(timeout);
  }, [search]);

  // Helper function to get full name
  const getFullName = (patient: Patient) => {
    return `${patient.firstName} ${patient.lastName}`.trim() || patient.username;
  };

  const filtered = patients;

  return (
    <div style={{ padding: '0' }}>
      <HeaderBar title="Mis Pacientes" />
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          justifyContent: 'space-between',
          gap: isMobile ? 12 : 0,
          marginBottom: isMobile ? 24 : 40,
        }}>
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Buscar por nombre, email, teléfono..."
            fullWidth={isMobile}
          />
          <button
            onClick={() => setShowCreateModal(true)}
            style={{
              background: '#38b2ac',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '10px 20px',
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              whiteSpace: 'nowrap',
              boxShadow: '0 2px 4px rgba(56, 178, 172, 0.2)',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#319795';
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(56, 178, 172, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#38b2ac';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(56, 178, 172, 0.2)';
            }}
          >
            <span style={{ fontSize: 20, lineHeight: 1 }}>+</span>
            <span>Nuevo Paciente</span>
          </button>
        </div>
        {loading ? (
          <div style={{ padding: 40, fontSize: 18, color: '#666', textAlign: 'center' }}>
            Loading patients...
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, fontSize: 18, color: '#666', textAlign: 'center' }}>
            No patients found
          </div>
        ) : (
          <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 14 }}>
                    PACIENTE
                  </th>
                  {!isMobile && (
                    <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 14 }}>
                      TELÉFONO
                    </th>
                  )}
                  <th style={{ padding: '16px 20px', textAlign: isMobile ? 'center' : 'left', fontWeight: 600, color: '#374151', fontSize: 14 }}>
                    ALERTAS
                  </th>
                  <th style={{ padding: '16px 20px', textAlign: 'center', fontWeight: 600, color: '#374151', fontSize: 14 }}>
                    SERVICIOS
                  </th>
                  <th style={{ padding: '16px 20px', textAlign: 'center', fontWeight: 600, color: '#374151', fontSize: 14 }}>
                    ACCIONES
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((patient: Patient, index: number) => (
                  <tr 
                    key={patient.id}
                    style={{ 
                      borderBottom: index < filtered.length - 1 ? '1px solid #f1f5f9' : 'none',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#f8fafc';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                    onClick={() => navigate(`/app/patients/${patient.id}`)}
                  >
                    <td style={{ padding: '16px 20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ 
                          width: 40, 
                          height: 40, 
                          borderRadius: '50%', 
                          background: patient.avatarUrl ? `url(${patient.avatarUrl})` : '#38b2ac',
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          fontSize: 16, 
                          fontWeight: 600, 
                          color: '#fff',
                          flexShrink: 0
                        }}>
                          {!patient.avatarUrl && getFullName(patient).charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                            <div style={{ fontWeight: 600, color: '#1f2937', fontSize: 16 }}>
                              {getFullName(patient)}
                            </div>
                            {patient.app_version && (
                              <span style={{ color: '#9ca3af', fontSize: 12 }}>
                                v{patient.app_version}
                              </span>
                            )}
                          </div>
                          <div style={{ color: '#6b7280', fontSize: 14 }} title={patient.email}>
                            {patient.email && patient.email.length > 30 ? `${patient.email.slice(0, 30)}…` : (patient.email || '-')}
                          </div>
                        </div>
                      </div>
                    </td>
                    {!isMobile && (
                      <td style={{ padding: '16px 20px', color: '#6b7280', fontSize: 14 }}>
                        {patient.phone_number || '-'}
                      </td>
                    )}
                    <td style={{ padding: '16px 20px', textAlign: isMobile ? 'center' : 'left' }}>
                      {(() => {
                        const status = getPatientActivityStatus(patient);
                        const config =
                          status === 'never'
                            ? { dot: '#ef4444', bg: '#fee2e2', color: '#991b1b', label: 'Nunca accedió' }
                            : status === 'inactive'
                            ? { dot: '#f59e0b', bg: '#fef3c7', color: '#92400e', label: 'Sin actividad +7 días' }
                            : null;
                        if (!config) return null;

                        if (isMobile) {
                          return (
                            <span
                              title={config.label}
                              aria-label={config.label}
                              style={{
                                display: 'inline-block',
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                background: config.dot,
                              }}
                            />
                          );
                        }

                        return (
                          <span style={{
                            display: 'inline-block',
                            background: config.bg,
                            color: config.color,
                            fontSize: 12,
                            fontWeight: 600,
                            padding: '4px 10px',
                            borderRadius: 12,
                            whiteSpace: 'nowrap',
                          }}>
                            {config.label}
                          </span>
                        );
                      })()}
                    </td>
                    <td style={{ padding: '16px 20px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                      <ServiceStatusIcons status={patient.serviceStatus} size={20} gap={6} />
                    </td>
                    <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                      <div
                        ref={(el) => { menuRefs.current[patient.id] = el; }}
                        style={{ position: 'relative', display: 'inline-block' }}
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveMenu(activeMenu === patient.id ? null : patient.id);
                          }}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '8px',
                            fontSize: '24px',
                            color: '#555',
                            lineHeight: 1,
                            borderRadius: '50%',
                            width: '36px',
                            height: '36px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'background 0.2s',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#f0f0f0';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                          }}
                        >
                          ⋮
                        </button>

                        {activeMenu === patient.id && (
                          <div
                            style={{
                              position: 'absolute',
                              right: 0,
                              top: '100%',
                              marginTop: '4px',
                              background: '#fff',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                              borderRadius: '8px',
                              minWidth: '260px',
                              padding: '8px 0',
                              zIndex: 100,
                            }}
                          >
                            <button
                              style={{
                                width: '100%',
                                padding: '12px 16px',
                                border: 'none',
                                background: 'transparent',
                                textAlign: 'left',
                                cursor: 'pointer',
                                fontSize: '15px',
                                color: '#222',
                                transition: 'background 0.2s',
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveMenu(null);
                                navigate(`/app/patients/${patient.id}`);
                              }}
                              onMouseOver={(e) => (e.currentTarget.style.background = '#f5f5f5')}
                              onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
                            >
                              Ver perfil
                            </button>

                            <button
                              style={{
                                width: '100%',
                                padding: '12px 16px',
                                border: 'none',
                                background: 'transparent',
                                textAlign: 'left',
                                cursor: 'pointer',
                                fontSize: '15px',
                                color: '#222',
                                transition: 'background 0.2s',
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowConfirmation({
                                  userId: patient.userId,
                                  type: 'welcome',
                                  patientName: getFullName(patient),
                                  patientEmail: patient.email,
                                });
                              }}
                              onMouseOver={(e) => (e.currentTarget.style.background = '#f5f5f5')}
                              onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
                            >
                              Enviar email de bienvenida sin reset password
                            </button>

                            <button
                              style={{
                                width: '100%',
                                padding: '12px 16px',
                                border: 'none',
                                background: 'transparent',
                                textAlign: 'left',
                                cursor: 'pointer',
                                fontSize: '15px',
                                color: '#222',
                                transition: 'background 0.2s',
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowConfirmation({
                                  userId: patient.userId,
                                  type: 'welcomeWithPassword',
                                  patientName: getFullName(patient),
                                  patientEmail: patient.email,
                                });
                              }}
                              onMouseOver={(e) => (e.currentTarget.style.background = '#f5f5f5')}
                              onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
                            >
                              Enviar email bienvenida con reset password
                            </button>

                            {canSendMonarkaEmails && (
                              <>
                                <button
                                  style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    border: 'none',
                                    background: 'transparent',
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                    fontSize: '15px',
                                    color: '#222',
                                    transition: 'background 0.2s',
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowConfirmation({
                                      userId: patient.userId,
                                      type: 'welcomeMonarka',
                                      patientName: getFullName(patient),
                                      patientEmail: patient.email,
                                    });
                                  }}
                                  onMouseOver={(e) => (e.currentTarget.style.background = '#f5f5f5')}
                                  onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
                                >
                                  Enviar email bienvenida Monarka sin reset password
                                </button>

                                <button
                                  style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    border: 'none',
                                    background: 'transparent',
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                    fontSize: '15px',
                                    color: '#222',
                                    transition: 'background 0.2s',
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowConfirmation({
                                      userId: patient.userId,
                                      type: 'welcomeMonarkaWithPassword',
                                      patientName: getFullName(patient),
                                      patientEmail: patient.email,
                                    });
                                  }}
                                  onMouseOver={(e) => (e.currentTarget.style.background = '#f5f5f5')}
                                  onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
                                >
                                  Enviar email bienvenida Monarka con reset password
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {!loading && filtered.length > 0 && totalPages > 1 && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            gap: '8px',
            marginTop: '24px',
            padding: '16px 0'
          }}>
            <button
              onClick={() => {
                const newPage = currentPage - 1;
                setCurrentPage(newPage);
                fetchPatients(newPage, search);
              }}
              disabled={currentPage === 1}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                background: currentPage === 1 ? '#f8fafc' : '#fff',
                color: currentPage === 1 ? '#94a3b8' : '#1f2937',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                fontWeight: 500,
                fontSize: '14px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                if (currentPage !== 1) {
                  e.currentTarget.style.background = '#f8fafc';
                  e.currentTarget.style.borderColor = '#cbd5e1';
                }
              }}
              onMouseLeave={(e) => {
                if (currentPage !== 1) {
                  e.currentTarget.style.background = '#fff';
                  e.currentTarget.style.borderColor = '#e2e8f0';
                }
              }}
            >
              Anterior
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                const isCurrentPage = page === currentPage;
                const showPage = 
                  page === 1 || 
                  page === totalPages || 
                  (page >= currentPage - 1 && page <= currentPage + 1);

                if (!showPage) {
                  if (page === currentPage - 2 || page === currentPage + 2) {
                    return (
                      <span key={page} style={{ padding: '0 4px', color: '#94a3b8' }}>
                        ...
                      </span>
                    );
                  }
                  return null;
                }

                return (
                  <button
                    key={page}
                    onClick={() => {
                      setCurrentPage(page);
                      fetchPatients(page, search);
                    }}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      background: isCurrentPage ? '#38b2ac' : '#fff',
                      color: isCurrentPage ? '#fff' : '#1f2937',
                      cursor: 'pointer',
                      fontWeight: isCurrentPage ? 600 : 500,
                      fontSize: '14px',
                      minWidth: '36px',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (!isCurrentPage) {
                        e.currentTarget.style.background = '#f8fafc';
                        e.currentTarget.style.borderColor = '#cbd5e1';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isCurrentPage) {
                        e.currentTarget.style.background = '#fff';
                        e.currentTarget.style.borderColor = '#e2e8f0';
                      }
                    }}
                  >
                    {page}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => {
                const newPage = currentPage + 1;
                setCurrentPage(newPage);
                fetchPatients(newPage, search);
              }}
              disabled={currentPage === totalPages}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                background: currentPage === totalPages ? '#f8fafc' : '#fff',
                color: currentPage === totalPages ? '#94a3b8' : '#1f2937',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                fontWeight: 500,
                fontSize: '14px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                if (currentPage !== totalPages) {
                  e.currentTarget.style.background = '#f8fafc';
                  e.currentTarget.style.borderColor = '#cbd5e1';
                }
              }}
              onMouseLeave={(e) => {
                if (currentPage !== totalPages) {
                  e.currentTarget.style.background = '#fff';
                  e.currentTarget.style.borderColor = '#e2e8f0';
                }
              }}
            >
              Siguiente
            </button>

            {/* <span style={{ 
              marginLeft: '16px', 
              color: '#6b7280', 
              fontSize: '14px',
              fontWeight: 500
            }}>
              Página {currentPage} de {totalPages}
            </span> */}
          </div>
        )}
      </div>

      {/* Create Patient Modal */}
      <CreatePatientModal
        open={showCreateModal}
        onSave={handleCreatePatient}
        onCancel={() => setShowCreateModal(false)}
      />

      {/* Confirmation Modal for Welcome Emails */}
      {showConfirmation && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={(e) => {
            e.stopPropagation();
            setShowConfirmation(null);
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '480px',
              width: '90%',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px 0', fontSize: '20px', fontWeight: 600, color: '#222' }}>
              Confirmar envío
            </h3>
            <p style={{ margin: '0 0 24px 0', fontSize: '15px', color: '#555', lineHeight: 1.5 }}>
              ¿Estás seguro de que deseas enviar un email de bienvenida
              {(showConfirmation.type === 'welcomeMonarka' || showConfirmation.type === 'welcomeMonarkaWithPassword') ? ' Monarka' : ''}
              {(showConfirmation.type === 'welcomeWithPassword' || showConfirmation.type === 'welcomeMonarkaWithPassword') ? ' con reset de password' : ' sin reset de password'}
              {' '}a <strong>{showConfirmation.patientName}</strong> ({showConfirmation.patientEmail})?
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: '1px solid #ddd',
                  background: '#fff',
                  color: '#555',
                  fontWeight: 600,
                  fontSize: '15px',
                  cursor: 'pointer',
                  transition: 'background 0.2s',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowConfirmation(null);
                }}
                disabled={isSending}
                onMouseOver={(e) => (e.currentTarget.style.background = '#f5f5f5')}
                onMouseOut={(e) => (e.currentTarget.style.background = '#fff')}
              >
                Cancelar
              </button>
              <button
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  background: '#38b2ac',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '15px',
                  cursor: isSending ? 'not-allowed' : 'pointer',
                  opacity: isSending ? 0.6 : 1,
                  transition: 'opacity 0.2s, background 0.2s',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSendWelcomeEmail(showConfirmation.userId, showConfirmation.type);
                }}
                disabled={isSending}
                onMouseOver={(e) => {
                  if (!isSending) e.currentTarget.style.background = '#319795';
                }}
                onMouseOut={(e) => {
                  if (!isSending) e.currentTarget.style.background = '#38b2ac';
                }}
              >
                {isSending ? 'Enviando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientsList; 